import { useState } from 'react';
import { Download, Printer } from 'lucide-react';
import { cn } from 'cn';
import { useProperties } from '@/features/properties/queries';
import { formatCents } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { buildBilanWorkbook, downloadWorkbook } from './excel';
import { LedgerTabs } from './LedgerTabs';
import { useAnnualSummary } from './queries';

const ALL = '__all__';

export function BilanPage() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [propertyId, setPropertyId] = useState<string>(ALL);
  const properties = useProperties();
  const summary = useAnnualSummary(year, propertyId === ALL ? undefined : Number(propertyId));

  async function exportExcel() {
    if (!summary.data) return;
    const workbook = await buildBilanWorkbook(summary.data);
    await downloadWorkbook(`bilan-${String(year)}.xlsx`, workbook);
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 print:hidden sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold">Ledger</h1>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={propertyId} onValueChange={setPropertyId}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tous les biens</SelectItem>
              {properties.data?.map((property) => (
                <SelectItem key={property.id} value={String(property.id)}>{property.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            type="button"
            aria-label="Année précédente"
            onClick={() => setYear((y) => y - 1)}
            className="rounded-md border border-input px-2 py-1 text-sm"
          >
            ←
          </button>
          <span className="w-16 text-center font-medium">{year}</span>
          <button
            type="button"
            aria-label="Année suivante"
            onClick={() => setYear((y) => y + 1)}
            className="rounded-md border border-input px-2 py-1 text-sm"
          >
            →
          </button>
          <Button variant="outline" onClick={exportExcel} disabled={!summary.data || summary.data.properties.length === 0}>
            <Download /> Export Excel
          </Button>
          <Button variant="outline" onClick={() => window.print()} disabled={!summary.data || summary.data.properties.length === 0}>
            <Printer /> Imprimer / PDF
          </Button>
        </div>
      </div>
      <h1 className="hidden text-2xl font-semibold print:block">Bilan annuel {year}</h1>
      <div className="print:hidden">
        <LedgerTabs />
      </div>

      {summary.isPending && <p className="text-muted-foreground">Chargement…</p>}
      {summary.isError && <p className="text-destructive">Impossible de charger le bilan.</p>}

      {summary.data && summary.data.properties.length === 0 && (
        <p className="text-muted-foreground">Aucune écriture validée pour {year}.</p>
      )}

      {summary.data?.properties.map((property) => (
        <div key={property.propertyId} className="flex flex-col gap-2 print:break-after-page">
          <h2 className="text-lg font-medium">{property.propertyName}</h2>
          <Card className="gap-0 overflow-hidden py-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/60 hover:bg-muted/60">
                <TableHead className="px-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:px-2">
                  Catégorie
                </TableHead>
                <TableHead className="px-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:px-2">
                  Sens
                </TableHead>
                <TableHead className="px-1.5 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:px-2">
                  Montant
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {property.categories.map((category) => (
                <TableRow key={category.categoryId}>
                  <TableCell className="max-w-32 truncate px-1.5 sm:max-w-none sm:px-2" title={category.label}>
                    {category.label}
                  </TableCell>
                  <TableCell
                    className={cn(
                      'px-1.5 font-medium sm:px-2',
                      category.direction === 'income' ? 'text-emerald-700' : 'text-destructive',
                    )}
                  >
                    {category.direction === 'income' ? 'Recette' : 'Dépense'}
                  </TableCell>
                  <TableCell
                    className={cn(
                      'px-1.5 text-right font-semibold tabular-nums sm:px-2',
                      category.direction === 'income' ? 'text-emerald-700' : 'text-destructive',
                    )}
                  >
                    {formatCents(category.totalCents, property.currency)}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className="font-medium">
                <TableCell colSpan={2} className="px-1.5 sm:px-2">Recettes totales</TableCell>
                <TableCell className="px-1.5 text-right tabular-nums text-emerald-700 sm:px-2">
                  {formatCents(property.incomeCents, property.currency)}
                </TableCell>
              </TableRow>
              <TableRow className="font-medium">
                <TableCell colSpan={2} className="px-1.5 sm:px-2">Dépenses totales</TableCell>
                <TableCell className="px-1.5 text-right tabular-nums text-destructive sm:px-2">
                  {formatCents(property.expenseCents, property.currency)}
                </TableCell>
              </TableRow>
              <TableRow className="border-t-2 font-semibold">
                <TableCell colSpan={2} className="px-1.5 sm:px-2">Résultat net</TableCell>
                <TableCell
                  className={cn(
                    'px-1.5 text-right tabular-nums sm:px-2',
                    property.netCents < 0 ? 'text-destructive' : 'text-emerald-700',
                  )}
                >
                  {formatCents(property.netCents, property.currency)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
          </Card>
        </div>
      ))}

      {summary.data && summary.data.properties.length > 0 && (
        <div className="flex flex-col gap-2 border-t border-border pt-4">
          <h2 className="text-lg font-medium">Consolidé</h2>
          <Card className="gap-0 overflow-hidden py-0">
          <Table>
            <TableBody>
              <TableRow>
                <TableCell className="px-1.5 sm:px-2">Recettes totales</TableCell>
                <TableCell className="px-1.5 text-right tabular-nums text-emerald-700 sm:px-2">
                  {formatCents(summary.data.consolidated.incomeCents, 'EUR')}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="px-1.5 sm:px-2">Dépenses totales</TableCell>
                <TableCell className="px-1.5 text-right tabular-nums text-destructive sm:px-2">
                  {formatCents(summary.data.consolidated.expenseCents, 'EUR')}
                </TableCell>
              </TableRow>
              <TableRow className="border-t-2 font-semibold">
                <TableCell className="px-1.5 sm:px-2">Résultat net</TableCell>
                <TableCell
                  className={cn(
                    'px-1.5 text-right tabular-nums sm:px-2',
                    summary.data.consolidated.netCents < 0 ? 'text-destructive' : 'text-emerald-700',
                  )}
                >
                  {formatCents(summary.data.consolidated.netCents, 'EUR')}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
          </Card>
        </div>
      )}
    </div>
  );
}
